// @ts-nocheck
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Divider,
  Typography,
  Grid,
  Radio,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, InfoOutlined } from '@mui/icons-material';
import { getIn, useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { history } from '../../helpers/history-helpers';
import {
  FBoxContainer,
  FCheckbox,
  FHButton,
  FHInputField,
  FHPageLink,
  FHSelect,
  FProgressBar,
} from '../../components/base';
import { payorActions, serviceCodeActions } from '../../store';
import PayorContractRow from '../components/PayorContractRow';
import PageContainer from '../../components/layout/PageContainer';
import PayorServiceCodeLabelRow from '../components/PayorServiceCodeLabelRow';
import { FORTA_BLUE, FORTA_ERROR, FORTA_GRAY2 } from '../../utils/colors';
import { BackgroundTypes } from '../../constants';
import {
  BTTypeOptions,
  defaultServiceCodeDescriptions,
  PayorSectorOptions,
  PayorTypeOptions,
} from '../constants/general';

import type { RootState } from '../../store';
import type { SelectChangeEvent } from '@mui/material';
import type { ChangeEvent } from 'react';
import type {
  IOption,
  IPayorContract,
  IServiceCodeDescription,
  SelectValueType,
} from '../../utils/types';

const inputFieldStyle = {
  boxShadow: 'none',
  '& .MuiInputBase-root': {
    border: `1px solid ${FORTA_GRAY2}`,
    borderRadius: '10px',
    '&:focus-within': {
      border: 'none',
    },
  },
};

const payorValidationSchema = Yup.object().shape({
  label: Yup.string().trim().required('Name is a required field'),
  state: Yup.string()
    .trim()
    .max(2, 'Max 2 Chars are allowed for State')
    .min(2, 'Min 2 Chars are required for State')
    .required('State is a required field'),
  clearinghouse_payor_id: Yup.string().trim().required('Payor id is a required field'),
  type: Yup.string().trim().required('Please choose a payor type'),
  sector: Yup.string().trim().required('Please choose a sector'),
  changehealthcare_eligibility_payor_id: Yup.string()
    .trim()
    .max(50, 'Max 50 characters are allowed')
    .nullable()
    .optional(),
  modifier: Yup.string()
    .trim()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .max(2, 'Modifier should either be empty or have 2 characters')
    .min(2, 'Modifier should either be empty or have 2 characters'),
  preferred_service_code_type: Yup.string()
    .trim()
    .required('Please choose a preferred service code type'),
  is_using_taxonomy_code: Yup.boolean().required('Please select Yes or No'),
  internal_classification: Yup.string().nullable().notRequired(),
  forta_website_payor_name: Yup.string().nullable().notRequired(),
  max_client_age: Yup.number()
    .typeError('Maximum client age must be a number')
    .nullable()
    .notRequired()
    .lessThan(51, 'Maximum client age must be less than 50')
    .positive('Maximum client age must be a positive number'),
  is_pbt_allowed: Yup.boolean().nullable().notRequired(),
  is_telehealth_allowed: Yup.boolean().nullable().notRequired(),
  bt_type: Yup.string().nullable().notRequired(),
  client_supervision_requirements: Yup.object().shape({
    is_required: Yup.boolean().required('Please select Yes or No'),
    criteria: Yup.object().when('is_required', ([isRequired]) => {
      if (isRequired) {
        return Yup.object().shape({
          operator: Yup.string().oneOf(['AND', 'OR']).required('Please select an operator'),
          //if the operator is AND, both fields are required, if the operator is OR, any one field is required
          minimum_percentage: Yup.number()
            .typeError('Minimum percentage must be a number')
            .when('operator', {
              is: 'AND',
              then: (schema) =>
                schema
                  .required('Please enter a minimum percentage')
                  .lessThan(1, 'Minimum percentage must be less than 100')
                  .positive('Minimum percentage must be a positive number'),
              otherwise: (schema) =>
                schema
                  .test(
                    'one-field-required',
                    'Either percentage or hours is required',
                    function (value) {
                      return value !== undefined || this.parent.minimum_hours !== undefined;
                    },
                  )
                  .nullable()
                  .transform((value) => (value === null ? undefined : value))
                  .lessThan(1, 'Minimum percentage must be less than 100')
                  .positive('Minimum percentage must be a positive number'),
            }),
          minimum_hours: Yup.number()
            .typeError('Minimum hours must be a number')
            .when('operator', {
              is: 'AND',
              then: (schema) =>
                schema
                  .required('Please enter minimum hours')
                  .positive('Minimum hours must be a positive number'),
              otherwise: (schema) =>
                schema
                  .test(
                    'one-field-required',
                    'Either percentage or hours is required',
                    function (value) {
                      return value !== undefined || this.parent.minimum_percentage !== undefined;
                    },
                  )
                  .nullable()
                  .transform((value) => (value === null ? undefined : value))
                  .positive('Minimum hours must be a positive number'),
            }),
        });
      }
      return Yup.mixed().nullable().notRequired();
    }),
  }),
});

const initialPayorState = {
  label: '',
  state: '',
  clearinghouse_payor_id: '',
  type: '',
  sector: '',
  modifier: null,
  preferred_service_code_type: '',
  is_using_taxonomy_code: null,
  changehealthcare_eligibility_payor_id: null,
  internal_classification: null, // optional field
  forta_website_payor_name: null, // optional field
  max_client_age: null, // optional field
  is_pbt_allowed: null,
  is_telehealth_allowed: null,
  by_type: '',
  client_supervision_requirements: {
    is_required: false, //not required by default
    criteria: {
      operator: null,
      minimum_hours: null,
      minimum_percentage: null,
    },
  },
};

function AddPayorForm() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { payorId } = useParams();
  const { current, loading: payorsLoading } = useSelector((state: RootState) => state.payors);
  const isDisabled = payorsLoading;
  const [contractList, setContractList] = useState<IPayorContract[]>([]);
  const [isNewContractAdding, setIsNewContractAdding] = useState(false);
  const [editableContractIndex, setEditableContractIndex] = useState<number | null>(null);
  const [editingServiceCodeLabelIndex, setEditingServiceCodeLabelIndex] = useState<number | null>(
    null,
  );

  const formik = useFormik({
    initialValues: initialPayorState,
    onSubmit: async (values: any) => {
      if (Object.keys(errors).length) {
        return; // return as form is not valid
      }
      let response: any = null;
      if (payorId) {
        response = await dispatch(
          payorActions.updatePayor({
            data: values,
            getAccessTokenSilently,
            payorId,
          }),
        );
      } else {
        response = await dispatch(
          payorActions.createPayor({
            data: values,
            getAccessTokenSilently,
          }),
        );
      }
      if (!response.error) {
        // update the existing payor list
        dispatch(payorActions.getList({ getAccessTokenSilently }));
        if (!payorId) {
          history.navigate(`/admin-add-payor/${response.payload.id}`);
        }
      }
    },
    validationSchema: payorValidationSchema,
  });

  const { values, setFieldValue, submitForm, errors, touched, setValues } = formik;

  useEffect(() => {
    if (!payorId) return;
    dispatch(
      serviceCodeActions.getList({
        getAccessTokenSilently,
        params: { is_timesheet_usable: false },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payorId]);

  useEffect(() => {
    if (!payorId) return;
    dispatch(payorActions.getPayorById({ payorId, getAccessTokenSilently }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payorId]);

  useEffect(() => {
    if (!payorId || !current) return;
    const { id: _id, taxonomy_code: _taxonomy_code, evv_enabled: _evv_enabled, ...rest } = current;
    setValues(rest);
    setContractList(rest.contracts); // Set the contract list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payorId, current]);

  const handleUpdateFieldValues = (key: string, value: any) => {
    if ((key === 'modifier' || key === 'changehealthcare_eligibility_payor_id') && value === '') {
      setFieldValue(key, null);
    } else {
      setFieldValue(key, value);
    }
  };

  const handleAddContract = () => {
    const newContract = {
      id: null,
      service_code_id: null,
      rate_per_unit: null,
      date_start: null,
      date_end: null,
    };

    setContractList([newContract, ...contractList]); // Add the new contract to the list
    setEditableContractIndex(0); // Set the index of the newly added contract
    setIsNewContractAdding(true); // Set the flag to indicate that a new contract is being added
  };

  const handleEditContract = (index: number) => {
    setEditableContractIndex(index); // Set the index of the contract being edited
  };

  const handleUpdateContract = async (index: number, updatedContract: IPayorContract) => {
    try {
      //If the contract has an id, we are updating an existing contract, otherwise we are creating a new one
      const response = updatedContract.id
        ? await dispatch(
            payorActions.updateContract({
              data: updatedContract,
              payorId,
              contractId: updatedContract.id,
              getAccessTokenSilently,
            }),
          )
        : await dispatch(
            payorActions.createContract({
              data: {
                service_code_id: updatedContract.service_code_id,
                rate_per_unit: updatedContract.rate_per_unit,
                date_start: updatedContract.date_start,
                date_end: updatedContract.date_end,
              },
              payorId,
              getAccessTokenSilently,
            }),
          );

      if (!response.error) {
        dispatch(payorActions.getPayorById({ payorId, getAccessTokenSilently }));
        setEditableContractIndex(null); // Reset the editable contract index
        setIsNewContractAdding(false); // Reset the flag for adding a new contract
      }
    } catch (error) {
      toast(`Error updating/creating contract:${error}`);
    }
  };

  const handleUpdateServiceCodeLabel = async (updatedServiceCodeLabel: IServiceCodeDescription) => {
    try {
      if (updatedServiceCodeLabel.id) {
        const response = await dispatch(
          payorActions.updateServiceCodeLabel({
            data: updatedServiceCodeLabel,
            payorId,
            serviceCodeDescriptionId: updatedServiceCodeLabel.id,
            getAccessTokenSilently,
          }),
        );
        if (!response.error) {
          setEditingServiceCodeLabelIndex(null);
        }
      } else {
        const response = await dispatch(
          payorActions.createServiceCodeLabel({
            data: updatedServiceCodeLabel,
            getAccessTokenSilently,
            payorId,
          }),
        );
        if (!response.error) {
          setEditingServiceCodeLabelIndex(null);
        }
      }
    } catch (error) {
      toast.error(`Error updating service code label: ${error}`);
    }
  };

  const handleResetServiceCodeLabel = async (updatedServiceCodeLabel: IServiceCodeDescription) => {
    if (!updatedServiceCodeLabel.service_code) return;
    await dispatch(
      payorActions.updateServiceCodeLabel({
        data: {
          ...updatedServiceCodeLabel,
          description:
            Object.entries(defaultServiceCodeDescriptions).find(
              ([serviceCode]) => serviceCode === updatedServiceCodeLabel.service_code,
            )?.[1] ?? updatedServiceCodeLabel.description,
        },
        payorId,
        serviceCodeDescriptionId: updatedServiceCodeLabel.id,
        getAccessTokenSilently,
      }),
    );
  };

  const handleDeleteContract = async (contract: IPayorContract) => {
    try {
      const response = await dispatch(
        payorActions.deleteContract({
          payorId,
          contractId: contract.id,
          getAccessTokenSilently,
        }),
      );
      if (!response.error) {
        setContractList((contracts) => contracts.filter((c) => c.id !== contract.id));
      }
    } catch (error) {
      toast(`Error deleting contract:${error}`);
    }
  };

  const handleCancelEditContract = () => {
    // If adding a new contract (identified by null id), remove it from the list
    setContractList((contracts) => contracts.filter((contract) => contract.id !== null));
    setEditableContractIndex(null); // Reset the editable contract index
    setIsNewContractAdding(false); // Reset the flag for adding a new contract
  };

  const ContractsTableHeader = () => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Grid container spacing={1} mb={1}>
        <Grid item xs={3}>
          <Typography textAlign={'left'} ml={1} fontWeight="bold">
            Service Code*
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography fontWeight="bold">Rate per unit*</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography fontWeight="bold">Start date*</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography fontWeight="bold">End date</Typography>
        </Grid>
        <Grid item xs={3}></Grid>
      </Grid>
    </Box>
  );

  const ServiceCodeLabelsTableHeader = () => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Grid container spacing={1} mb={1}>
        <Grid item xs={1.5}>
          <Typography textAlign={'left'} mx={3} fontWeight="bold">
            Service Code*
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography textAlign={'left'} ml={4} fontWeight="bold">
            Description*
          </Typography>
        </Grid>
        <Grid item xs={1}></Grid>
        <Grid item xs={1}></Grid>
      </Grid>
    </Box>
  );

  return (
    <PageContainer>
      <Box sx={{ mt: 3, mb: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        {payorsLoading && <FProgressBar />}
        <FBoxContainer sx={{ borderRadius: '20px', boxShadow: 'none' }}>
          <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mb: 2 }}>
            <Typography textAlign="left" variant="h4">
              Payor Form
            </Typography>
          </Box>
          <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Name:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHInputField
                        inputProps={
                          {
                            'data-testid': 'addPayorLabel',
                          }
                        }
                        value={values['label']}
                        inputHeight={40}
                        sx={inputFieldStyle}
                        disabled={isDisabled}
                        fullWidth
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues('label', e.target.value);
                        }}
                        error={touched.label && Boolean(errors.label)}
                      />
                      {touched.label && errors.label && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.label}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>State:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHInputField
                        inputProps={
                          {
                            'data-testid': 'addPayorState',
                          }
                        }
                        value={values['state']}
                        inputHeight={40}
                        sx={inputFieldStyle}
                        disabled={isDisabled}
                        fullWidth
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues('state', e.target.value);
                        }}
                        error={touched.state && Boolean(errors.state)}
                      />
                      {touched.state && errors.state && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.state}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Modifier:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHInputField
                        inputProps={
                          {
                            'data-testid': 'addPayorModifier',
                          }
                        }
                        value={values['modifier'] || ''}
                        fullWidth
                        sx={inputFieldStyle}
                        disabled={isDisabled}
                        inputHeight={40}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues('modifier', e.target.value);
                        }}
                        error={touched.modifier && Boolean(errors.modifier)}
                      />
                      {touched.modifier && errors.modifier && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.modifier}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Is using taxonomy code:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <RadioGroup
                        row
                        value={values['is_using_taxonomy_code']}
                        name="taxonomy-code-radio-group"
                        onChange={(e: SelectChangeEvent<unknown>) => {
                          handleUpdateFieldValues('is_using_taxonomy_code', e.target.value);
                        }}
                      >
                        <FormControlLabel
                          data-testid="addPayorTaxonomyEnabled"
                          value={true}
                          disabled={isDisabled}
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          data-testid="addPayorTaxonomyDisabled"
                          value={false}
                          disabled={isDisabled}
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                      {touched.is_using_taxonomy_code && Boolean(errors.is_using_taxonomy_code) && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.is_using_taxonomy_code}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Clearinghouse Payor Id:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHInputField
                        inputProps={
                          {
                            'data-testid': 'addPayorPayorId',
                          }
                        }
                        value={values['clearinghouse_payor_id']}
                        fullWidth
                        inputHeight={40}
                        sx={inputFieldStyle}
                        disabled={isDisabled}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues('clearinghouse_payor_id', e.target.value);
                        }}
                        error={
                          touched.clearinghouse_payor_id && Boolean(errors.clearinghouse_payor_id)
                        }
                      />
                      {touched.clearinghouse_payor_id && errors.clearinghouse_payor_id && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.clearinghouse_payor_id}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>
                        Change-Healthcare eligibility payor id:
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHInputField
                        value={values['changehealthcare_eligibility_payor_id']}
                        fullWidth
                        inputHeight={40}
                        sx={inputFieldStyle}
                        disabled={isDisabled}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues(
                            'changehealthcare_eligibility_payor_id',
                            e.target.value || null,
                          );
                        }}
                        error={
                          touched.changehealthcare_eligibility_payor_id &&
                          Boolean(errors.changehealthcare_eligibility_payor_id)
                        }
                      />
                      {touched.changehealthcare_eligibility_payor_id &&
                        errors.changehealthcare_eligibility_payor_id && (
                          <FormHelperText
                            sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                          >
                            {errors.changehealthcare_eligibility_payor_id}
                          </FormHelperText>
                        )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Payor Type:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHSelect
                        data-testid="addPayorType"
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        fullWidth
                        disabled={isDisabled}
                        sx={{
                          boxShadow: 'none',
                          border: `0.5px solid ${FORTA_GRAY2}`,
                          borderRadius: '10px',
                          '&:focus-within': {
                            border: 'none',
                          },
                        }}
                        options={PayorTypeOptions.map((payorType: IOption) => ({
                          value: payorType.label,
                          label: payorType.label,
                        }))}
                        optionDataTestIdPrefix="addPayorType"
                        value={values['type']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('type', value);
                        }}
                        error={touched.type && Boolean(errors.type)}
                      />
                      {touched.type && errors.type && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.type}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Sector:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHSelect
                        data-testid="addSector"
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        fullWidth
                        disabled={isDisabled}
                        sx={{
                          boxShadow: 'none',
                          border: `0.5px solid ${FORTA_GRAY2}`,
                          borderRadius: '10px',
                          '&:focus-within': {
                            border: 'none',
                          },
                        }}
                        options={PayorSectorOptions.map((payorSector: IOption) => ({
                          value: payorSector.label,
                          label: payorSector.label,
                        }))}
                        optionDataTestIdPrefix="addPayorSector"
                        value={values['sector']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('sector', value);
                        }}
                        error={touched.sector && Boolean(errors.sector)}
                      />
                      {touched.sector && errors.sector && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.sector}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Preferred Service Code Type:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHSelect
                        data-testid="addPayorSCTypeSelect"
                        inputHeight={40}
                        sx={{
                          boxShadow: 'none',
                          border: `0.5px solid ${FORTA_GRAY2}`,
                          borderRadius: '10px',
                          '&:focus-within': {
                            border: 'none',
                          },
                        }}
                        backgroundType={BackgroundTypes.White}
                        fullWidth
                        disabled={isDisabled}
                        options={[
                          { value: 'CPT', label: 'CPT' },
                          { value: 'HCPCS', label: 'HCPCS' },
                        ]}
                        optionDataTestIdPrefix="addPayorSCTypeCPT"
                        value={values['preferred_service_code_type']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('preferred_service_code_type', value);
                        }}
                        error={
                          touched.preferred_service_code_type &&
                          Boolean(errors.preferred_service_code_type)
                        }
                      />
                      {touched.preferred_service_code_type &&
                        errors.preferred_service_code_type && (
                          <FormHelperText
                            sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                          >
                        {errors.preferred_service_code_type}
                          </FormHelperText>
                        )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Internal classification:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHInputField
                        inputProps={
                          {
                            'data-testid': 'addPayorInternalClassification',
                          }
                        }
                        value={values['internal_classification']}
                        fullWidth
                        inputHeight={40}
                        sx={inputFieldStyle}
                        disabled={isDisabled}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues(
                            'internal_classification',
                            e.target.value || null,
                          );
                        }}
                        error={
                          touched.internal_classification && Boolean(errors.internal_classification)
                        }
                      />
                      {touched.internal_classification && errors.internal_classification && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.internal_classification}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ ml: 3, display: { xs: 'none', md: 'block' } }}
              />
              <Grid item xs={12} md={5.7} lg={5.7}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Forta website payor name:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHInputField
                        inputProps={
                          {
                            'data-testid': 'addPayorFortaWebsitePayorName',
                          }
                        }
                        value={values['forta_website_payor_name']}
                        fullWidth
                        inputHeight={40}
                        sx={inputFieldStyle}
                        disabled={isDisabled}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues(
                            'forta_website_payor_name',
                            e.target.value || null,
                          );
                        }}
                        error={
                          touched.forta_website_payor_name &&
                          Boolean(errors.forta_website_payor_name)
                        }
                      />
                      {touched.forta_website_payor_name && errors.forta_website_payor_name && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.forta_website_payor_name}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Maximum client age:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <FHInputField
                        type="number"
                        inputProps={
                          {
                            'data-testid': 'addPayorMaximumClientAge',
                          }
                        }
                        value={values['max_client_age']}
                        inputHeight={40}
                        sx={{
                          ...inputFieldStyle,
                          width: '140px',
                          minWidth: '140px',
                        }}
                        disabled={isDisabled}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          handleUpdateFieldValues('max_client_age', e.target.value || null);
                        }}
                        error={touched.max_client_age && Boolean(errors.max_client_age)}
                      />
                      {touched.max_client_age && errors.max_client_age && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.max_client_age}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} mb={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Is PBT allowed:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={2} md={1}>
                      <FCheckbox
                        data-testid="addPayorPBTAllowed"
                        checked={values['is_pbt_allowed'] === true}
                        disabled={isDisabled}
                        onChange={(e) => {
                          handleUpdateFieldValues('is_pbt_allowed', e.target.checked);
                        }}
                      />
                      {touched.is_pbt_allowed && Boolean(errors.is_pbt_allowed) && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.is_pbt_allowed}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container p={1} spacing={1} mb={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Is Telehealth allowed:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={2} md={1}>
                      <FCheckbox
                        data-testid="addPayorTelehealthAllowed"
                        checked={values['is_telehealth_allowed'] === true}
                        disabled={isDisabled}
                        onChange={(e) => {
                          handleUpdateFieldValues('is_telehealth_allowed', e.target.checked);
                        }}
                      />
                      {touched.is_telehealth_allowed && Boolean(errors.is_telehealth_allowed) && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.is_telehealth_allowed}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>

                  <Grid container p={1} spacing={1} mb={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>BT Type:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                      <FHSelect
                        data-testid="addPayorBTType"
                        inputHeight={40}
                        backgroundType={BackgroundTypes.White}
                        fullWidth
                        disabled={isDisabled}
                        sx={{
                          boxShadow: 'none',
                          border: `0.5px solid ${FORTA_GRAY2}`,
                          borderRadius: '10px',
                          '&:focus-within': {
                            border: 'none',
                          },
                        }}
                        options={BTTypeOptions.map((payorType: IOption) => ({
                          value: payorType.label,
                          label: payorType.label,
                        }))}
                        optionDataTestIdPrefix="addPayorBTTypeOption"
                        value={values['bt_type']}
                        onUpdate={(value: SelectValueType) => {
                          handleUpdateFieldValues('bt_type', value);
                        }}
                        error={touched.bt_type && Boolean(errors.bt_type)}
                      />
                      {touched.bt_type && errors.bt_type && (
                        <FormHelperText
                          sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                        >
                          {errors.bt_type}
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>

                  <Grid container p={1} spacing={1} direction="row" alignItems="center">
                    <Grid item xs={12} sm={4} md={4}>
                      <Typography textAlign={'left'}>Specific CSN utilization required:</Typography>
                    </Grid>
                    <Grid item xs={12} sm={8} md={8}>
                      <RadioGroup
                        row
                        value={values.client_supervision_requirements?.is_required}
                        name="client-supervision-criteria-required-radio-group"
                        onChange={(e: SelectChangeEvent<unknown>) => {
                          const isRequired = e.target.value === 'true';
                          handleUpdateFieldValues('client_supervision_requirements', {
                            ...values.client_supervision_requirements,
                            is_required: isRequired,
                            criteria: isRequired
                              ? {
                                  operator:
                                    values.client_supervision_requirements?.criteria?.operator,
                                  minimum_percentage:
                                    values.client_supervision_requirements?.criteria
                                      ?.minimum_percentage || null,
                                  minimum_hours:
                                    values.client_supervision_requirements?.criteria
                                      ?.minimum_hours || null,
                                }
                              : null,
                          });
                        }}
                      >
                        <FormControlLabel
                          data-testid="addPayorClientSupervisionCriteriaRequired"
                          value={true}
                          disabled={isDisabled}
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          data-testid="addPayorClientSupervisionCriteriaNotRequired"
                          value={false}
                          disabled={isDisabled}
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                      {getIn(touched, 'client_supervision_requirements.is_required') &&
                        getIn(errors, 'client_supervision_requirements.is_required') && (
                          <FormHelperText
                            sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                          >
                            {getIn(errors, 'client_supervision_requirements.is_required')}
                          </FormHelperText>
                        )}
                    </Grid>
                    {values.client_supervision_requirements?.is_required === false && (
                      <Grid item xs={12} sm={12} md={12} mt={1}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <InfoOutlined
                            sx={{ mr: 1, mt: 0.2, fontSize: '24px', color: FORTA_BLUE.primary }}
                          />
                          <Typography
                            textAlign={'left'}
                            fontSize={'15px'}
                            color={FORTA_BLUE.primary}
                          >
                            A minimum of 10% of session note hours will be applied as required hours
                            for client supervision.
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  {values.client_supervision_requirements?.is_required === true && (
                    <>
                      <Grid container p={1} spacing={1} direction="row" alignItems="center">
                        <Grid item xs={12} sm={4} md={4}>
                          <Typography textAlign={'left'}>
                            Minimum utilization percentage :
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8} md={8}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FHInputField
                              type="number"
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                              value={
                                values.client_supervision_requirements?.criteria.minimum_percentage
                                  ? Math.round(
                                      values.client_supervision_requirements?.criteria
                                        .minimum_percentage * 100,
                                    )
                                  : null
                              }
                              inputHeight={40}
                              sx={{
                                ...inputFieldStyle,
                                width: '140px',
                                minWidth: '140px',
                              }}
                              disabled={isDisabled}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                handleUpdateFieldValues('client_supervision_requirements', {
                                  ...values.client_supervision_requirements,
                                  criteria: {
                                    ...values.client_supervision_requirements?.criteria,
                                    minimum_percentage: e.target.value
                                      ? Number(e.target.value) / 100
                                      : null,
                                  },
                                });
                              }}
                              error={
                                getIn(
                                  touched,
                                  'client_supervision_requirements.criteria.minimum_percentage',
                                ) &&
                                Boolean(
                                  getIn(
                                    errors,
                                    'client_supervision_requirements.criteria.minimum_percentage',
                                  ),
                                )
                              }
                            />
                            <Typography>of total session hours</Typography>
                          </Box>
                          {getIn(
                            touched,
                            'client_supervision_requirements.criteria.minimum_percentage',
                          ) &&
                            getIn(
                              errors,
                              'client_supervision_requirements.criteria.minimum_percentage',
                            ) && (
                              <FormHelperText
                                sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                              >
                                {getIn(
                                  errors,
                                  'client_supervision_requirements.criteria.minimum_percentage',
                                )}
                              </FormHelperText>
                            )}
                        </Grid>
                      </Grid>
                      <Grid container p={1} spacing={1} direction="row" alignItems="center">
                        <Grid item xs={12} sm={4} md={4}>
                          <Typography textAlign={'left'}>Operator:</Typography>
                        </Grid>
                        <Grid item xs={12} sm={8} md={8}>
                          <RadioGroup
                            row
                            value={values.client_supervision_requirements.criteria.operator}
                            name="operator-radio-group"
                            onChange={(e: SelectChangeEvent<unknown>) => {
                              handleUpdateFieldValues('client_supervision_requirements', {
                                ...values.client_supervision_requirements,
                                criteria: {
                                  ...values.client_supervision_requirements?.criteria,
                                  operator: e.target.value,
                                },
                              });
                            }}
                          >
                            <FormControlLabel
                              data-testid="addPayorOperatorAND"
                              value={'AND'}
                              disabled={isDisabled}
                              control={<Radio />}
                              label="AND"
                            />
                            <FormControlLabel
                              data-testid="addPayorOperatorOR"
                              value={'OR'}
                              disabled={isDisabled}
                              control={<Radio />}
                              label="OR"
                            />
                          </RadioGroup>
                          {getIn(touched, 'client_supervision_requirements.criteria.operator') &&
                            getIn(errors, 'client_supervision_requirements.criteria.operator') && (
                              <FormHelperText
                                sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                              >
                                {getIn(
                                  errors,
                                  'client_supervision_requirements.criteria.operator',
                                )}
                              </FormHelperText>
                            )}
                        </Grid>
                      </Grid>
                      <Grid container p={1} spacing={1} direction="row" alignItems="center">
                        <Grid item xs={12} sm={4} md={4}>
                          <Typography textAlign={'left'}>Minimum utilization hours :</Typography>
                        </Grid>
                        <Grid item xs={12} sm={8} md={8}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FHInputField
                              type="number"
                              InputProps={{
                                endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                              }}
                              value={
                                values.client_supervision_requirements?.criteria.minimum_hours
                                  ? values.client_supervision_requirements?.criteria.minimum_hours /
                                    3600
                                  : null
                              }
                              fullWidth
                              inputHeight={40}
                              sx={{
                                ...inputFieldStyle,
                                width: '140px',
                                minWidth: '140px',
                              }}
                              disabled={isDisabled}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                handleUpdateFieldValues('client_supervision_requirements', {
                                  ...values.client_supervision_requirements,
                                  criteria: {
                                    ...values.client_supervision_requirements?.criteria,
                                    minimum_hours: e.target.value
                                      ? Number(e.target.value) * 3600
                                      : null,
                                  },
                                });
                              }}
                              error={
                                getIn(
                                  touched,
                                  'client_supervision_requirements.criteria.minimum_hours',
                                ) &&
                                Boolean(
                                  getIn(
                                    errors,
                                    'client_supervision_requirements.criteria.minimum_hours',
                                  ),
                                )
                              }
                            />
                            <Typography>per week</Typography>
                          </Box>
                          {getIn(
                            touched,
                            'client_supervision_requirements.criteria.minimum_hours',
                          ) &&
                            getIn(
                              errors,
                              'client_supervision_requirements.criteria.minimum_hours',
                            ) && (
                              <FormHelperText
                                sx={{ color: FORTA_ERROR, textAlign: 'left', fontSize: '14px' }}
                              >
                                {getIn(
                                  errors,
                                  'client_supervision_requirements.criteria.minimum_hours',
                                )}
                              </FormHelperText>
                            )}
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Box ml={1} sx={{ display: 'flex' }}>
                  <FHButton
                    data-testid="addPayorSubmit"
                    sx={{
                      width: 150,
                      height: 45,
                      borderRadius: '8px',
                    }}
                    onClick={submitForm}
                    disabled={isDisabled}
                  >
                    Submit
                  </FHButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FBoxContainer>
        {payorId && (
          <FBoxContainer sx={{ borderRadius: '20px', mt: 3, boxShadow: 'none' }}>
            <Grid container>
              <Grid item xs={12} md={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box ml={1} mb={2} sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography textAlign={'left'} variant="h4">
                      Service Code Labels
                    </Typography>
                  </Box>
                  <ServiceCodeLabelsTableHeader />
                  {current?.service_code_descriptions?.map((serviceCodeLabel, index) => (
                    <PayorServiceCodeLabelRow
                      key={serviceCodeLabel.service_code_id}
                      preferredServiceCodeType={values['preferred_service_code_type']}
                      isEditing={editingServiceCodeLabelIndex === index}
                      isDisabled={isDisabled}
                      serviceCode={serviceCodeLabel}
                      onEdit={() => setEditingServiceCodeLabelIndex(index)}
                      onCancel={() => setEditingServiceCodeLabelIndex(null)}
                      onSave={(updatedServiceCodeLabel) =>
                        handleUpdateServiceCodeLabel(updatedServiceCodeLabel)
                      }
                      onReset={(updatedServiceCodeLabel) =>
                        handleResetServiceCodeLabel(updatedServiceCodeLabel)
                      }
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </FBoxContainer>
        )}

        {payorId && (
          <FBoxContainer sx={{ borderRadius: '20px', mt: 3, boxShadow: 'none' }}>
            <Grid container>
              <Grid item xs={12} md={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box ml={1} mb={2} sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography textAlign={'left'} variant="h4">
                      Contracts
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                    <FHButton
                      data-testid="startNewContractButton"
                      sx={{
                        width: 200,
                        height: 45,
                        borderRadius: '16px',
                      }}
                      startIcon={<AddIcon />}
                      disabled={isNewContractAdding}
                      onClick={handleAddContract}
                    >
                      Add Contract
                    </FHButton>
                  </Box>
                  {contractList && contractList.length > 0 && <ContractsTableHeader />}
                  {contractList && contractList.length > 0 ? (
                    contractList.map((contract, index) => (
                      <PayorContractRow
                        key={contract.id}
                        contract={contract}
                        isAdding={isNewContractAdding && index === 0}
                        isEditing={editableContractIndex === index}
                        isDisabled={isDisabled}
                        onSave={(updatedContract) => handleUpdateContract(index, updatedContract)}
                        onCancel={handleCancelEditContract}
                        onEdit={() => handleEditContract(index)}
                        onDelete={(contract: IPayorContract) => handleDeleteContract(contract)}
                      />
                    ))
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        m: 2,
                      }}
                    >
                      <Typography variant="h6">No contracts found for this payor</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </FBoxContainer>
        )}
      </Box>
    </PageContainer>
  );
}

export default AddPayorForm;
